import type { FormEvent } from 'react'

import { useCallback, useMemo, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  DepartmentDTO,
  ListDepartmentsParams,
  ListDirectoratesParams,
} from '@/utils/api/user-service/models'

import { OrganisationSearchInput } from '@/components/common/OrganisationSearchInput'
import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { AnimatedSwitch } from '@/components/ui/animated-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import { handleError } from '@/lib/utils'
import {
  useGetAllOrganisations,
  useListDepartments,
  useListDirectorates,
  useUpsertDepartment,
} from '@/utils/api/user-service'

const DEFAULT_FORM_STATE = {
  id: '',
  code: '',
  name: '',
  description: '',
  organisationId: '',
  directorateId: '',
  hrApprovalDepartment: false,
  ceoApprovalDepartment: false,
  active: true,
}

export function DepartmentsPage() {
  const { user: currentUser } = useAuth()
  const isAdmin = isAdminUser(currentUser)
  // const isOrgAdmin = isOrganisationAdminUser(currentUser)
  const showOrganisationFilter = isAdmin
  const shouldSelectOrganisation = isAdmin

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE)
  const [organisationFilter, setOrganisationFilter] = useState('all')
  const selectedOrganisationId = useMemo(() => {
    if (!showOrganisationFilter || organisationFilter === 'all') {
      return undefined
    }

    const parsedOrganisationId = Number(organisationFilter)
    return Number.isNaN(parsedOrganisationId) ? undefined : parsedOrganisationId
  }, [organisationFilter, showOrganisationFilter])
  const activeOrganisationId = useMemo(() => {
    const parsedFormOrganisationId = Number(formState.organisationId)
    if (formState.organisationId.trim() !== '' && !Number.isNaN(parsedFormOrganisationId)) {
      return parsedFormOrganisationId
    }

    return selectedOrganisationId
  }, [formState.organisationId, selectedOrganisationId])

  const listParams = useMemo<ListDepartmentsParams>(() => ({
    page: 0,
    size: 25,
    organisationId: selectedOrganisationId,
  }), [selectedOrganisationId])
  const directoratesParams = useMemo<ListDirectoratesParams>(() => ({
    page: 0,
    size: 100,
    organisationId: activeOrganisationId,
  }), [activeOrganisationId])
  const listQuery = useListDepartments(listParams)
  const directoratesQuery = useListDirectorates(directoratesParams)
  const organisationsQuery = useGetAllOrganisations({
    query: {
      enabled: showOrganisationFilter,
      staleTime: 30_000,
    },
  })
  const upsertMutation = useUpsertDepartment({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogMode('create')
        setDialogOpen(false)
        void listQuery.refetch()
      },
      onError: (e: import('axios').AxiosError) => handleError(e, 'Unable to save department. Please try again.'),
    },
  })

  const departments = useMemo(() => listQuery.data?.data?.content ?? [], [listQuery.data])
  const directorates = useMemo(() => directoratesQuery.data?.data?.content ?? [], [directoratesQuery.data])
  const organisations = useMemo(() => organisationsQuery.data?.data ?? [], [organisationsQuery.data])
  const canEditOrganisation = isAdmin
  const showOrganisationSelect = canEditOrganisation
  const organisationSelectionRequired = shouldSelectOrganisation && dialogMode === 'create'
  const canSubmit = formState.code.trim() !== ''
    && formState.name.trim() !== ''
    && (!organisationSelectionRequired
      || formState.organisationId.trim() !== ''
      || organisationFilter !== 'all')
  const filteredDepartments = useMemo(() => {
    if (selectedOrganisationId === undefined) {
      return departments
    }
    return departments.filter(department => department.organisationId === selectedOrganisationId)
  }, [departments, selectedOrganisationId])
  const directorateOptions = useMemo(() => (
    directorates
      .filter(item => activeOrganisationId === undefined || item.organisationId === activeOrganisationId)
      .filter(item => typeof item.id === 'number')
      .map(item => ({
        id: item.id?.toString() ?? '',
        name: String(item.name ?? item.code ?? item.id ?? ''),
      }))
  ), [activeOrganisationId, directorates])
  const organisationOptions = useMemo(() => (
    organisations
      .filter(item => typeof item.id === 'number')
      .map(item => ({
        id: item.id?.toString() ?? '',
        name: item.name ?? `Organisation ${item.id ?? ''}`,
      }))
  ), [organisations])
  const organisationFilterOptions = useMemo(() => (
    [{ id: 'all', name: 'All organisations' }, ...organisationOptions]
  ), [organisationOptions])
  const organisationFilterControl = showOrganisationFilter
    ? (
      <OrganisationSearchInput
        id="admin-organisation"
        label=""
        placeholder={organisationsQuery.isPending ? 'Loading organisations...' : 'Select organisation'}
        value={organisationFilter}
        onValueChange={value => setOrganisationFilter(value)}
        options={organisationFilterOptions}
        isLoading={organisationsQuery.isPending}
        disabled={organisationsQuery.isPending || organisationsQuery.isError}
      />
    )
    : undefined

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || upsertMutation.isPending) {
      return
    }

    const parsedId = formState.id.trim() === '' ? undefined : Number(formState.id)
    const parsedDirectorateId = formState.directorateId.trim() === ''
      ? undefined
      : Number(formState.directorateId)
    const parsedOrganisationId = canEditOrganisation
      ? (formState.organisationId.trim() !== ''
        ? Number(formState.organisationId)
        : (dialogMode === 'create' && organisationFilter !== 'all' ? Number(organisationFilter) : undefined))
      : undefined

    if (organisationSelectionRequired
      && (parsedOrganisationId === undefined || Number.isNaN(parsedOrganisationId))) {
      return
    }
    const payload: DepartmentDTO = {
      id: parsedId === undefined || Number.isNaN(parsedId) ? undefined : parsedId,
      code: formState.code.trim() || undefined,
      name: formState.name.trim() || undefined,
      description: formState.description.trim() || undefined,
      active: formState.active,
      hrApprovalDepartment: formState.hrApprovalDepartment,
      ceoApprovalDepartment: formState.ceoApprovalDepartment,
    }

    if (parsedDirectorateId !== undefined && !Number.isNaN(parsedDirectorateId)) {
      payload.directorate = { id: parsedDirectorateId }
    }

    if (canEditOrganisation && parsedOrganisationId !== undefined && !Number.isNaN(parsedOrganisationId)) {
      const org = organisationOptions.find(org => org.id === parsedOrganisationId.toString())
      payload.organisationId = parsedOrganisationId
      payload.organisation = org?.name || undefined
    }

    upsertMutation.mutate({ data: payload })
  }

  const openCreateDialog = useCallback(() => {
    setFormState({
      ...DEFAULT_FORM_STATE,
      organisationId: organisationFilter !== 'all' ? organisationFilter : DEFAULT_FORM_STATE.organisationId,
      directorateId: DEFAULT_FORM_STATE.directorateId,
    })
    setDialogMode('create')
    setDialogOpen(true)
  }, [organisationFilter])

  const handleEdit = useCallback((item: DepartmentDTO) => {
    setFormState({
      id: item.id?.toString() ?? '',
      code: item.code ?? '',
      name: item.name ?? '',
      description: item.description ?? '',
      organisationId: item.organisationId?.toString() ?? '',
      directorateId: item.directorate?.id?.toString() ?? '',
      hrApprovalDepartment: item.hrApprovalDepartment ?? false,
      ceoApprovalDepartment: item.ceoApprovalDepartment ?? false,
      active: item.active ?? true,
    })
    setDialogMode('edit')
    setDialogOpen(true)
  }, [])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setDialogMode('create')
      setFormState(DEFAULT_FORM_STATE)
    }
  }, [])

  const dialogTitle = dialogMode === 'create' ? 'Create Department' : 'Edit Department'
  const dialogDescription = dialogMode === 'create'
    ? 'Add a new department and assign its directorate.'
    : 'Update the selected department.'
  const submitLabel = dialogMode === 'create' ? 'Create Department' : 'Save Changes'

  const columns = useMemo<ColumnConfig<DepartmentDTO>[]>(() => ([
    {
      key: 'code',
      header: 'Code',
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'organisation',
      header: 'Organisation',
      cell: value => value as string || '-',
    },
    {
      key: 'directorateName',
      header: 'Directorate',
      cell: value => value as string || '-',
    },
    {
      key: 'hrApprovalDepartment',
      header: 'HR Approval',
      type: 'boolean',
    },
    {
      key: 'ceoApprovalDepartment',
      header: 'CEO Approval',
      type: 'boolean',
    },
    {
      key: 'active',
      header: 'Active',
      type: 'boolean',
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ]), [handleEdit])

  return (
    <div className="space-y-6">
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="department-name">Name</Label>
                <Input
                  id="department-name"
                  value={formState.name}
                  onChange={event => setFormState(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="Finance"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="department-description">Description</Label>
                <Input
                  id="department-description"
                  value={formState.description}
                  onChange={event => setFormState(prev => ({ ...prev, description: event.target.value }))}
                  placeholder="Optional department description"
                />
              </div>
              {showOrganisationSelect && (
                <div className="space-y-2 md:col-span-2">
                  <OrganisationSearchInput
                    id="department-organisation"
                    label="Organisation"
                    placeholder={organisationsQuery.isPending ? 'Loading organisations...' : 'Select organisation'}
                    value={formState.organisationId}
                    onValueChange={value => setFormState(prev => ({ ...prev, organisationId: value, directorateId: '' }))}
                    options={organisationOptions}
                    isLoading={organisationsQuery.isPending}
                    disabled={organisationsQuery.isPending || organisationsQuery.isError}
                    required={organisationSelectionRequired}
                  />
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-5 md:col-span-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="department-code">Code</Label>
                  <Input
                    id="department-code"
                    value={formState.code}
                    onChange={event => setFormState(prev => ({ ...prev, code: event.target.value }))}
                    placeholder="FIN"
                  />
                </div>
                <div className="space-y-2 md:col-span-3">
                  <SearchSelect
                    id="department-directorate"
                    label="Directorate"
                    placeholder={directoratesQuery.isPending ? 'Loading directorates...' : 'Select directorate'}
                    value={formState.directorateId}
                    onValueChange={value => setFormState(prev => ({ ...prev, directorateId: value }))}
                    options={directorateOptions}
                    isLoading={directoratesQuery.isPending}
                    disabled={directoratesQuery.isPending || directoratesQuery.isError}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 md:col-span-2">
                <div className="flex items-center justify-between rounded-lg border px-4 py-3 md:col-span-1">
                  <div>
                    <Label htmlFor="department-hr-approval">HR Approval</Label>
                    <p className="text-sm text-muted-foreground">Flag as HR approval department.</p>
                  </div>
                  <AnimatedSwitch
                    id="department-hr-approval"
                    checked={formState.hrApprovalDepartment}
                    onCheckedChange={checked => setFormState(prev => ({ ...prev, hrApprovalDepartment: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border px-4 py-3 md:col-span-1">
                  <div>
                    <Label htmlFor="department-ceo-approval">CEO Approval</Label>
                    <p className="text-sm text-muted-foreground">Flag as CEO approval department.</p>
                  </div>
                  <AnimatedSwitch
                    id="department-ceo-approval"
                    checked={formState.ceoApprovalDepartment}
                    onCheckedChange={checked => setFormState(prev => ({ ...prev, ceoApprovalDepartment: checked }))}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border px-4 py-3 md:col-span-2">
                <div>
                  <Label htmlFor="department-active">Active</Label>
                  <p className="text-sm text-muted-foreground">Enable department for use.</p>
                </div>
                <AnimatedSwitch
                  id="department-active"
                  checked={formState.active}
                  onCheckedChange={checked => setFormState(prev => ({ ...prev, active: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || upsertMutation.isPending}>
                {upsertMutation.isPending ? 'Saving...' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Departments</CardTitle>
            <CardDescription>Review department metadata and directorate assignments.</CardDescription>
          </div>
          <Button type="button" onClick={openCreateDialog}>
            Create Department
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {listQuery.isPending && (
            <p className="text-sm text-muted-foreground">Loading departments...</p>
          )}
          <EnhancedDataTable
            data={filteredDepartments}
            columns={columns}
            loading={listQuery.isPending}
            emptyStateMessage="No departments found."
            enableColumnFilters={false}
            enableExport={false}
            customButtons={organisationFilterControl}
          />
        </CardContent>
      </Card>
    </div>
  )
}
